import {
    Component,
    effect,
    isSignal,
    Renderer2,
    ViewContainerRef,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    inputBinding,
    Binding,
    outputBinding,
    input,
    computed,
    EffectCleanupRegisterFn,
    Type,
    signal,
    isWritableSignal,
    twoWayBinding,
} from "@angular/core";
import { Table } from "./Table";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Bind } from "../../decorators/bind.decorator";
import { SpreadsheetCell } from "../s25-spreadsheet/spreadsheet.service";
import { S25TableUtil } from "./s25.table.util";

@TypeManagerDecorator("s25-ng-table-cell")
@Component({
    selector: "s25-ng-table-cell",
    template: `
        @if (!hidden() && !!config()?.text) {
            <span>{{ config().text }}</span>
        }
        <!-- Component cells are inserted AFTER the actual table cell component -->
    `,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
})
export class S25TableCellComponent implements SpreadsheetCell {
    readonly column = input<Table.Column>();
    readonly defaults = input<Table.Cell>();
    readonly overrides = input<Table.Cell>();
    readonly row = input<Table.Row>();

    readonly config = computed(() => {
        // If both are defined, combine
        if (this.overrides() && this.defaults()) return { ...this.defaults(), ...this.overrides() };
        // Return whichever is defined
        return this.overrides() ?? this.defaults();
    });
    readonly hidden = computed(() => this.config()?.if === false);

    readonly instance = signal<unknown>(undefined);

    constructor(
        private viewContainerRef: ViewContainerRef,
        private renderer: Renderer2,
    ) {
        effect(this.renderCell);
    }

    @Bind
    renderCell(onCleanup: EffectCleanupRegisterFn) {
        // Skip if no component was passed or the cell is hidden
        if (!this.config() || !this.config()?.component || this.hidden()) return;

        const { component, inputs, twoWayInputs, outputs, events } = this.config();
        this.viewContainerRef.clear();

        // Set up bindings
        const context = {
            column: this.column(),
            row: this.row(),
            cell: this.overrides(),
            instance: <Type<unknown>>undefined,
        };
        const bindings: Binding[] = [];
        for (const [input, value] of Object.entries(inputs ?? {})) {
            if (isSignal(value)) bindings.push(inputBinding(input, value));
            else bindings.push(inputBinding(input, () => value));
        }
        for (const [input, value] of Object.entries(twoWayInputs ?? {})) {
            if (isWritableSignal(value)) bindings.push(twoWayBinding(input, value));
            else console.error(`Invalid twoWayInput: "${input}" is not a writable signal.`);
        }
        for (const [output, handler] of Object.entries(outputs ?? {})) {
            bindings.push(outputBinding(output, (event) => handler(event, context)));
        }

        // Create component
        const componentRef = this.viewContainerRef.createComponent(component, { bindings });
        context.instance = componentRef.instance;
        this.instance.set(componentRef.instance);
        if (this.row() && this.column()) {
            this.row().cells[this.column().id] ??= { ...this.column().cells }; // Just needs to be a shallow copy
            this.row().cells[this.column().id].instance = componentRef.instance;
        }

        // Apply event listeners
        for (const [eventType, handler] of Object.entries(events ?? {})) {
            const stop = this.renderer.listen(componentRef.location.nativeElement, eventType, (event: unknown) =>
                handler(event, context),
            );
            onCleanup(stop);
        }
    }

    @Bind
    getSpreadsheetCellValue(): ReturnType<SpreadsheetCell["getSpreadsheetCellValue"]> {
        return S25TableUtil.getCellSpreadsheetValue({ ...this.config(), instance: this.instance() });
    }
}
